import type { MediaUploadAdapter, MediaUploadResult } from "./uploadAdapter";
import { localIndexedDbAdapter } from "./localIndexedDbAdapter";
import { MediaUploadError } from "./signTypes";

export interface HybridUploadOptions {
  local?: MediaUploadAdapter;
  onFallback?: (err: unknown) => void;
  /** default true — set false to surface cloud errors without IndexedDB rescue */
  allowLocalFallback?: boolean;
}

let lastHybridFallbackError: string | null = null;

export function takeHybridFallbackError(): string | null {
  const v = lastHybridFallbackError;
  lastHybridFallbackError = null;
  return v;
}

export function noteHybridFallbackError(err: unknown): void {
  lastHybridFallbackError =
    err instanceof MediaUploadError
      ? `Cloud unavailable (${err.stage}): ${err.message}`
      : err instanceof Error
        ? `Cloud unavailable: ${err.message}`
        : "Cloud unavailable — saved on this device only";
}

/**
 * Tries the real sign/PUT cloud adapter first.
 * On failure (unreachable Worker, 401, PUT error), falls back to IndexedDB
 * so onboarding stays testable while the endpoint is flaky.
 */
export function createHybridUploadAdapter(
  cloud: MediaUploadAdapter,
  options?: HybridUploadOptions
): MediaUploadAdapter {
  const local = options?.local ?? localIndexedDbAdapter;
  const allowFallback = options?.allowLocalFallback !== false;

  return {
    id: "hybrid",

    async upload(input): Promise<MediaUploadResult> {
      try {
        const result = await cloud.upload(input);
        lastHybridFallbackError = null;
        return { ...result, via: result.via ?? "cloud" };
      } catch (err) {
        noteHybridFallbackError(err);
        options?.onFallback?.(err);
        if (!allowFallback) throw err;
        const localResult = await local.upload(input);
        return { ...localResult, via: "local-fallback" };
      }
    },

    async getPreviewUrl(storageKey) {
      const remote = await cloud.getPreviewUrl(storageKey);
      if (remote) return remote;
      return local.getPreviewUrl(storageKey);
    },

    async delete(storageKey) {
      await Promise.allSettled([
        cloud.delete?.(storageKey),
        local.delete?.(storageKey),
      ]);
    },
  };
}
